import type { GridProps } from '@chakra-ui/react'
import { Box, Flex, Text, useToken } from '@chakra-ui/react'
import type { InsurerStats } from 'BrokerTsLib/src/dtos'
import { motion } from 'framer-motion'
import { useState } from 'react'

import { useInsurers } from '@/hooks'
import { useTranslationsContext } from 'BrokerTsLib/src/context/TranslationsContext'
import { containerVariants, MotionStack, springConfig } from './animations'
import { CollapseButton } from './CollapseButton'
import { CompanyRow } from './CompanyRow'
import {
  getCompanyLabel,
  prepareChartData
} from './dataUtils'
import { ExpandedCompaniesList } from './ExpandedCompaniesList'
import { OthersRow } from './OthersRow'


export interface InsuranceCompaniesStatsProps extends GridProps {
  companies?: InsurerStats[]
  selectedMetric: 'count' | 'totalValue'
}

export const InsuranceCompaniesStats = ({
  companies,
  selectedMetric = 'count',
  ...gridProps
}: InsuranceCompaniesStatsProps) => {
  const { data } = useInsurers(null, true)
  const insurers = data?.data ?? []
  const [isExpanded, setIsExpanded] = useState(false)
  const textColor = useToken('colors', 'chakra-body-text')
  const { translate } = useTranslationsContext()

  const { allChartData, topCompanies, othersData } = prepareChartData(
    companies,
    selectedMetric,
    insurers
  )

  if (!companies || companies.length === 0) {
    return (
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={springConfig}
      >
        <Flex justify="center" align="center" h="200px" {...gridProps}>
          <Text color={textColor}>{translate('noData', 'Žádná data k zobrazení')}</Text>
        </Flex>
      </motion.div>
    )
  }

  // Calculate max value for bar scaling and max label length
  const maxValue = Math.max(...allChartData.map(item => item.value))
  const maxLabelLength = allChartData.reduce((max, item) => {
    const label = getCompanyLabel(item, selectedMetric)
    return label.length > max ? label.length : max
  }, 0)

  return (
    <Box {...gridProps}>
      <MotionStack
        spacing={2}
        variants={containerVariants}
        initial="hidden"
        animate="visible"
      >
        {/* Render top companies (always visible) */}
        {topCompanies?.map((company) => {
          const percentage = (company.value / maxValue) * 100
          const label = getCompanyLabel(company, selectedMetric)

          return (
            <CompanyRow
              key={company.name}
              company={company}
              percentage={percentage}
              label={label}
              maxLabelLength={maxLabelLength}
              insurers={insurers}
              textColor={textColor}
              selectedMetric={selectedMetric}
            />
          )
        })}

        {/* Render "Others" row if there are companies to group */}
        {othersData && !isExpanded && (
          <OthersRow
            company={othersData}
            percentage={(othersData.value / maxValue) * 100}
            label={getCompanyLabel(othersData, selectedMetric)}
            maxLabelLength={maxLabelLength}
            isExpanded={isExpanded}
            onToggleExpanded={() => setIsExpanded(!isExpanded)}
            textColor={textColor}
            selectedMetric={selectedMetric}
          />
        )}

        {/* Render expanded companies with staggered animation */}
        {isExpanded && othersData?.companies && (
          <ExpandedCompaniesList
            companies={othersData.companies}
            selectedMetric={selectedMetric}
            maxValue={maxValue}
            maxLabelLength={maxLabelLength}
            insurers={insurers}
            textColor={textColor}
          />
        )}

        {/* Render "Others" collapse button at the end when expanded */}
        {isExpanded && othersData && (
          <CollapseButton
            othersData={othersData}
            onCollapse={() => setIsExpanded(false)}
          />
        )}
      </MotionStack>
    </Box>
  )
}
